import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Group, Loader, Text, Title } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { useRemittanceInstruction } from '../hooks/useRemittanceInstruction.ts';
import { RemittanceDetail } from '../models/remittance-instruction.ts';
import { RemittanceFeeMode } from '../models/remittance-fee-mode.ts';
import useDownloadRemittanceInstructionReport from '../hooks/useDownloadRemittanceInstructionReport.ts';
import { notifications } from '@mantine/notifications';
import { closeAllModals, openModal } from '@mantine/modals';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import RemittanceInstructionConfirmModal from '../components/modal/remittance-instruction/RemittanceInstructionConfirmModal.tsx';

export default function RemittanceInstructionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoading, data: remittanceInstruction } =
    useRemittanceInstruction(id);
  const { mutate: downloadReport } = useDownloadRemittanceInstructionReport();

  // 計算小計
  const totalAmount = useMemo(() => {
    if (remittanceInstruction?.remittanceDetails?.length === 0) return 0;
    return (
      remittanceInstruction?.remittanceDetails?.reduce(
        (acc, val) => acc + val.amount,
        0,
      ) || 0
    );
  }, [remittanceInstruction]);

  const columns: DataTableColumn<RemittanceDetail>[] = [
    {
      title: '對帳單',
      accessor: 'id',
      render: (row) => (
        <div>
          <Text>{row.parkingBilling.name}</Text>
        </div>
      ),
    },
    {
      title: '銀行/分行/代碼',
      accessor: 'bank_info',
      render: ({ beneficiary }) => (
        <Box>
          <Text>
            {beneficiary.bankName}/{beneficiary.bankBranchName}
          </Text>
          <Text size="sm" c="dimmed">
            {beneficiary.fullBankCode}
          </Text>
        </Box>
      ),
    },
    {
      title: '戶名/帳號',
      accessor: 'account_info',
      render: ({ beneficiary }) => (
        <Box>
          <Text>{beneficiary.accountName}</Text>
          <Text size="sm" c="dimmed">
            {beneficiary.accountNumber}
          </Text>
        </Box>
      ),
      footer: '總匯總金額',
    },
    {
      title: '匯款金額',
      accessor: 'amount',
      render: ({ amount }) => amount.toLocaleString(),
      footer: totalAmount.toLocaleString(),
    },
    {
      title: '手續費模式',
      accessor: 'feeMode',
      render: ({ remittanceFeeMode }) =>
        remittanceFeeMode === RemittanceFeeMode.BEN ? '內扣' : '不內扣',
    },
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <PreviousPageButton
        href={'/remittance-instructions'}
        content="返回匯款單列表"
      />
      <Paper p="xl" radius="lg" shadow="xl" mb="xl">
        <Title order={4} mb="xs">
          匯款單號：{remittanceInstruction.sequenceNumber}
        </Title>
        <DataTable
          idAccessor="id"
          highlightOnHover
          minHeight={150}
          columns={columns}
          records={remittanceInstruction?.remittanceDetails || []}
          verticalSpacing="xs"
          fetching={isLoading}
        />

        <Group mt="md">
          <Button
            onClick={() => {
              openModal({
                title: '確認已匯款',
                children: (
                  <RemittanceInstructionConfirmModal
                    remittanceInstruction={remittanceInstruction}
                    onSuccess={() => {
                      notifications.show({
                        title: '成功',
                        message: `匯款單 ${remittanceInstruction.sequenceNumber} 已成功確認匯款`,
                        color: 'green',
                      });
                      closeAllModals();
                      navigate(-1);
                    }}
                  />
                ),
              });
            }}
          >
            確認已匯款
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              downloadReport(remittanceInstruction.id, {
                onSuccess: () => {
                  notifications.show({
                    title: '下載成功',
                    message: `匯款單 ${remittanceInstruction.sequenceNumber} 下載成功`,
                    color: 'green',
                  });
                },
              });
            }}
          >
            下載
          </Button>
        </Group>
      </Paper>
    </>
  );
}
